import { useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { BacktestComparisonResult, ComparisonEvent } from '@/api/mediumTermBacktest';
import PortfolioSelector from './PortfolioSelector';
import {
  ACTION_LABELS,
  constraintText,
  dailyResult,
  eventReason,
  formatNumber,
  formatQuantity,
  formatSigned,
  portfolioOptions,
  shortDate,
} from './viewModel';
import type { PortfolioKey } from './viewModel';

interface StrategyActionLineProps {
  result: BacktestComparisonResult;
  portfolio: PortfolioKey;
  onPortfolioChange: (value: PortfolioKey) => void;
}

function initialTargetDate(path: ReturnType<typeof portfolioOptions>[number]['path']): string {
  return path.events.find((event) => event.filled_quantity_mwh > 0)?.target_date
    ?? path.daily_results[0]?.target_date
    ?? '';
}

function FilledAction({ event, role }: { event: ComparisonEvent; role: BacktestComparisonResult['role'] }) {
  return (
    <div className="mtb-action-row">
      <div className="mtb-action-row__identity">
        <strong>{event.window_index ? `W${event.window_index}` : '基线'}</strong>
        <span>{shortDate(event.trade_date)}</span>
        <b>{ACTION_LABELS[event.action]}</b>
      </div>
      <div className="mtb-action-row__facts">
        <span>{formatQuantity(event.filled_quantity_mwh)} MWh</span>
        <span>成交价 {formatNumber(event.execution_price)}</span>
        <span>相对基线 {event.spread == null ? '-' : formatSigned(event.spread)}</span>
        <span>仓位 {(event.target_position_before * 100).toFixed(1)}% → {(event.target_position_after * 100).toFixed(1)}%</span>
        <span>已实现 {formatSigned(event.realized_pnl_delta)} 元</span>
      </div>
      <p>{eventReason(event, role)}</p>
    </div>
  );
}

export default function StrategyActionLine({
  result,
  portfolio,
  onPortfolioChange,
}: StrategyActionLineProps) {
  const options = useMemo(() => portfolioOptions(result), [result]);
  const selectedPath = options.find((option) => option.key === portfolio)?.path ?? options[0].path;
  const dates = selectedPath.daily_results.map((day) => day.target_date);
  const [selectedDate, setSelectedDate] = useState(() => initialTargetDate(selectedPath));

  useEffect(() => {
    setSelectedDate(initialTargetDate(selectedPath));
  }, [selectedPath]);

  const option: EChartsOption = useMemo(() => ({
    animation: false,
    tooltip: { trigger: 'axis' },
    legend: { top: 0, right: 6, itemWidth: 14, textStyle: { color: '#475467', fontSize: 11 } },
    grid: { left: 64, right: 68, top: 42, bottom: 44 },
    xAxis: {
      type: 'category',
      data: selectedPath.daily_results.map((day) => shortDate(day.target_date)),
      axisLabel: { color: '#667085', fontSize: 10 },
    },
    yAxis: [
      { type: 'value', name: '仓位 MWh', min: 0, splitLine: { lineStyle: { color: '#edf0f3' } } },
      { type: 'value', name: '元/MWh', splitLine: { show: false } },
    ],
    series: [
      {
        name: '最终仓位',
        type: 'bar',
        barMaxWidth: 20,
        data: selectedPath.daily_results.map((day) => ({
          value: day.position_mwh,
          itemStyle: { color: day.target_date === selectedDate ? '#0b675f' : '#7db7ad' },
        })),
      },
      {
        name: '持仓均价',
        type: 'line',
        yAxisIndex: 1,
        connectNulls: false,
        symbolSize: 5,
        data: selectedPath.daily_results.map((day) => day.position_average_price),
        lineStyle: { color: '#a66c00', width: 2 },
      },
      {
        name: '月度基线价',
        type: 'line',
        yAxisIndex: 1,
        symbol: 'none',
        data: selectedPath.daily_results.map((day) => day.baseline_price),
        lineStyle: { color: '#667085', width: 1, type: 'dashed' },
      },
      {
        name: '现货价',
        type: 'line',
        yAxisIndex: 1,
        symbol: 'none',
        data: selectedPath.daily_results.map((day) => day.spot_price),
        lineStyle: { color: '#c34a36', width: 1.5 },
      },
    ],
  }), [selectedDate, selectedPath]);

  const chartEvents = useMemo(() => ({
    click: (params: { dataIndex?: number }) => {
      if (params.dataIndex != null && dates[params.dataIndex]) setSelectedDate(dates[params.dataIndex]);
    },
  }), [dates]);

  const day = dailyResult(selectedPath, selectedDate);
  const selectedEvents = selectedPath.events
    .filter((event) => event.target_date === selectedDate)
    .sort((a, b) => a.sequence_no - b.sequence_no);
  const filledEvents = selectedEvents.filter((event) => event.filled_quantity_mwh > 0);
  const constrainedEvents = selectedEvents.filter((event) => event.filled_quantity_mwh <= 0 || event.unfilled_quantity_mwh > 0);

  return (
    <section className="mtb-action-line" aria-labelledby="mtb-action-title">
      <div className="mtb-section-head mtb-section-head--controls">
        <div>
          <span className="mtb-kicker">标的日行动线</span>
          <h3 id="mtb-action-title">仓位由哪个窗口、什么理由形成</h3>
        </div>
        <PortfolioSelector
          options={options}
          value={portfolio}
          onChange={onPortfolioChange}
          ariaPrefix="行动线组合"
        />
      </div>

      <div className="mtb-chart-shell">
        <div className="mtb-chart-title">
          <strong>最终仓位、持仓均价与结算价格</strong>
          <span>点击柱或下方日期查看逐窗口行动；空仓日不绘制持仓均价</span>
        </div>
        <ReactECharts option={option} onEvents={chartEvents} style={{ height: 320 }} notMerge />
        <div className="mtb-day-picker" role="group" aria-label="标的日行动选择">
          {dates.map((date) => (
            <button
              key={date}
              type="button"
              className={date === selectedDate ? 'is-active' : ''}
              aria-label={`查看标的日行动 ${date}`}
              aria-pressed={date === selectedDate}
              onClick={() => setSelectedDate(date)}
            >
              {shortDate(date)}
            </button>
          ))}
        </div>
      </div>

      <div className="mtb-selected-day" data-testid="selected-day-action">
        <div className="mtb-selected-day__head">
          <strong>{selectedDate || '-'} 行动</strong>
          <span>{filledEvents.length} 笔实际成交</span>
        </div>
        {filledEvents.length
          ? filledEvents.map((event) => <FilledAction key={event.event_id} event={event} role={result.role} />)
          : <div className="mtb-no-action">该标的日没有实际成交</div>}

        {constrainedEvents.length ? (
          <details className="mtb-constrained-events">
            <summary>受限或未执行信号（{constrainedEvents.length}）</summary>
            {constrainedEvents.map((event) => (
              <div key={event.event_id}>
                W{event.window_index} · {shortDate(event.trade_date)} · {ACTION_LABELS[event.action]} · 请求 {formatQuantity(event.requested_quantity_mwh)} MWh · {constraintText(event)}
              </div>
            ))}
          </details>
        ) : null}

        {day ? (
          <div className="mtb-day-settlement">
            <span>最终仓位 {formatQuantity(day.position_mwh)} MWh</span>
            <span>持仓均价 {day.position_average_price == null ? '-' : formatNumber(day.position_average_price)}</span>
            <span>现货价 {formatNumber(day.spot_price)}</span>
            <span>已实现盈亏 {formatSigned(day.realized_pnl)} 元</span>
            <span>终值盈亏 {formatSigned(day.terminal_pnl)} 元</span>
            <strong>标的日总盈亏 {formatSigned(day.pnl)} 元</strong>
          </div>
        ) : null}
      </div>
    </section>
  );
}
